import { useCallback, useEffect, useRef, useState } from "react";
import { Pause, Play, Volume2, VolumeX } from "lucide-react";
import videoSource from "@/assets/videos/neil-video-1.mp4";
import videoPoster from "@/assets/videos/autonomous-workforce-video-poster.webp";

const AutonomousWorkforceHeroVideo = () => {
  const containerRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const manualPauseRef = useRef(false);
  const inViewportRef = useRef(false);
  const reducedMotionRef = useRef(false);
  const [autoplayEnabled, setAutoplayEnabled] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);

  const requestPlayback = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    setIsMuted(true);
    try {
      await video.play();
      setAutoplayBlocked(false);
    } catch {
      setAutoplayBlocked(true);
    }
  }, []);

  useEffect(() => {
    reducedMotionRef.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setAutoplayEnabled(!reducedMotionRef.current);
    const node = containerRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        inViewportRef.current = entry.isIntersecting;
        if (entry.isIntersecting) {
          if (!manualPauseRef.current && !reducedMotionRef.current && document.visibilityState === "visible") {
            void requestPlayback();
          }
        } else {
          videoRef.current?.pause();
        }
      },
      { rootMargin: "300px 0px", threshold: 0.15 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [requestPlayback]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        videoRef.current?.pause();
      } else if (inViewportRef.current && !manualPauseRef.current && !reducedMotionRef.current) {
        void requestPlayback();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [requestPlayback]);

  const togglePlayback = async () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      manualPauseRef.current = false;
      try {
        await video.play();
        setAutoplayBlocked(false);
      } catch {
        setAutoplayBlocked(true);
      }
    } else {
      manualPauseRef.current = true;
      video.pause();
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  return (
    <section ref={containerRef} aria-labelledby="workforce-video-heading" className="bg-[#030913] px-4 pb-12 pt-3 sm:px-6 md:pb-16 md:pt-5">
      <div className="mx-auto max-w-5xl text-center">
        <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#d7b96f]">Neil AI in the field</p>
        <h2 id="workforce-video-heading" className="mt-2 font-orbitron text-[1.35rem] font-bold leading-tight text-white min-[390px]:text-2xl sm:text-3xl md:text-4xl">
          See the Autonomous Workforce in Action
        </h2>

        <div className="relative mx-auto mt-6 w-full max-w-[430px] overflow-hidden rounded-2xl border border-cyan-400/60 bg-black shadow-[0_0_36px_rgba(34,211,238,0.18)]">
          <video
            ref={videoRef}
            poster={videoPoster}
            preload="none"
            autoPlay={autoplayEnabled}
            loop
            muted
            playsInline
            aria-label="Neil AI commercial cleaning robot operating in a warehouse"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onVolumeChange={(event) => setIsMuted(event.currentTarget.muted)}
            className="block h-auto w-full"
          >
            <source src={videoSource} type="video/mp4" />
          </video>

          {!isPlaying && (
            <button
              type="button"
              onClick={togglePlayback}
              className="absolute inset-0 flex items-center justify-center bg-black/20 text-white transition-colors hover:bg-black/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-300"
              aria-label={autoplayBlocked ? "Autoplay was blocked. Play video" : "Play video"}
            >
              <span className="flex h-16 w-16 items-center justify-center rounded-full border border-cyan-300/80 bg-[#061425]/90 shadow-[0_0_30px_rgba(34,211,238,0.35)]">
                <Play className="ml-1 h-7 w-7" fill="currentColor" aria-hidden="true" />
              </span>
            </button>
          )}

          <div className="absolute bottom-3 left-3 flex items-center gap-2">
            <button type="button" onClick={togglePlayback} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-[#030913]/90 text-white backdrop-blur transition hover:border-cyan-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300" aria-label={isPlaying ? "Pause video" : "Play video"}>
              {isPlaying ? <Pause className="h-5 w-5" aria-hidden="true" /> : <Play className="ml-0.5 h-5 w-5" aria-hidden="true" />}
            </button>
            <button type="button" onClick={toggleMute} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-[#030913]/90 text-white backdrop-blur transition hover:border-cyan-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300" aria-label={isMuted ? "Unmute video" : "Mute video"}>
              {isMuted ? <VolumeX className="h-5 w-5" aria-hidden="true" /> : <Volume2 className="h-5 w-5" aria-hidden="true" />}
            </button>
          </div>
        </div>
        <p className="mt-3 text-xs text-slate-400">Video starts muted. Use the controls to pause or enable sound.</p>
      </div>
    </section>
  );
};

export default AutonomousWorkforceHeroVideo;